import { useState, useEffect, useRef } from 'react';

// Counts a number up from 0 to `target` using requestAnimationFrame with an
// ease-out curve, re-triggering whenever `target` changes — e.g. the moment
// real data replaces a 0/loading placeholder, or a chart's data reloads.
// Shared by StatCard, Monthly Breakdown and Monthly Revenue so every number
// on the dashboard animates in the same way.
export const useCountUp = (target, duration = 900) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const numericTarget = Number(target) || 0;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(numericTarget * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(numericTarget);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return value;
};

// Renders `value` counting up, formatted with `format` (defaults to a plain
// rounded integer with thousands separators).
const AnimatedNumber = ({ value, format = (v) => Math.round(v).toLocaleString(), duration = 900 }) => {
  const animated = useCountUp(value, duration);
  return format(animated);
};

export default AnimatedNumber;
